import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';

// Vista previa de "qué ha recibido el cliente" para un correo ya enviado.
// Solo hay copia guardada (campo `html`) para los enviados a partir de
// 2026-09-11 — antes no se guardaba el contenido, solo destinatario/asunto/estado.
function EmailPreviewModal({ id, onClose }) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setLoading(true); setError(''); setData(null);
    fetch(`/.netlify/functions/getEmailsEnviados?id=${encodeURIComponent(id)}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        if (cancelado) return;
        if (!d.success) throw new Error(d.error || 'No se pudo cargar el correo');
        setData(d);
      })
      .catch(e => { if (!cancelado) setError(e.message); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">{data?.email?.subject || 'Correo enviado'}</h3>
            {data?.email?.to && <p className="text-xs text-gray-400 truncate mt-0.5">Para: {data.email.to}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none flex-shrink-0">✕</button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          {loading && <p className="text-sm text-gray-400 text-center py-16">Cargando…</p>}
          {error && <p className="text-sm text-red-600 text-center py-16">{error}</p>}
          {data && !data.tieneCopia && (
            <p className="text-sm text-gray-400 text-center py-16 max-w-sm mx-auto">
              Este correo se envió antes de que empezáramos a guardar una copia del contenido — no hay vista previa disponible para este envío.
            </p>
          )}
          {data?.tieneCopia && (
            <iframe
              title="Vista previa del correo tal y como lo recibió el cliente"
              srcDoc={data.email.html}
              sandbox=""
              className="w-full border border-gray-200 rounded-xl bg-white"
              style={{ height: '65vh' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Visor del registro de correos a clientes (emails_enviados) — observabilidad.
// Cada intento de envío (_emailClient.sendEmail) deja un doc: enviado /
// rechazado por el servidor / error. La colección es read:false en reglas; se
// consulta vía getEmailsEnviados.
export default function EmailsAdminView() {
  const [data, setData]       = useState({ emails: [], resumen: null });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [q, setQ]             = useState('');
  const [outbox, setOutbox]   = useState(null); // { resumen, eventos }
  const [previewId, setPreviewId] = useState(null);

  const cargar = useCallback(async (soloP) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`/.netlify/functions/getEmailsEnviados?limit=400${soloP ? '&soloProblemas=true' : ''}`, { headers: { ...authService.authHeader() } });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al cargar');
      setData({ emails: d.emails || [], resumen: d.resumen || null });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarOutbox = useCallback(async () => {
    try {
      const r = await fetch('/.netlify/functions/getEventosComunicacion?limit=400&soloProblemas=true', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      if (d.success) setOutbox({ resumen: d.resumen, eventos: d.eventos || [] });
    } catch { /* panel opcional */ }
  }, []);

  useEffect(() => { cargar(soloProblemas); }, [cargar, soloProblemas]);
  useEffect(() => { cargarOutbox(); }, [cargarOutbox]);

  const fecha = s => (s ? new Date(s).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
  const filtrados = data.emails.filter(e => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (e.to || '').toLowerCase().includes(s) || (e.subject || '').toLowerCase().includes(s) || (e.tipo || '').toLowerCase().includes(s);
  });

  const CHIP = {
    enviado:    'bg-green-100 text-green-700',
    rechazado:  'bg-amber-100 text-amber-800',
    error:      'bg-red-100 text-red-700',
    suprimido:  'bg-gray-200 text-gray-600',
    pendiente:  'bg-blue-100 text-blue-700',
    procesado:  'bg-green-100 text-green-700',
    descartado: 'bg-gray-100 text-gray-500',
  };
  const r7 = data.resumen?.ultimos7;
  const r30 = data.resumen?.ultimos30;

  const StatCard = ({ titulo, r }) => (
    <div className="bg-white rounded-2xl border shadow-sm p-4">
      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{titulo}</h3>
      {!r || r.total === 0 ? (
        <p className="text-sm text-gray-400">Sin envíos registrados</p>
      ) : (
        <div className="flex gap-4">
          <div><p className="text-2xl font-bold text-green-600 leading-none">{r.enviado || 0}</p><p className="text-[11px] text-gray-400 mt-1">enviados</p></div>
          <div><p className={`text-2xl font-bold leading-none ${(r.rechazado || 0) > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{r.rechazado || 0}</p><p className="text-[11px] text-gray-400 mt-1">rechazados</p></div>
          <div><p className={`text-2xl font-bold leading-none ${(r.error || 0) > 0 ? 'text-red-600' : 'text-gray-300'}`}>{r.error || 0}</p><p className="text-[11px] text-gray-400 mt-1">con error</p></div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Correos a clientes</h1>
          <p className="text-sm text-gray-500">Cada envío queda registrado: entregado al servidor, rechazado o con error.</p>
        </div>
        <button onClick={() => cargar(soloProblemas)} disabled={loading}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl">
          {loading ? '…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

      <div className="grid sm:grid-cols-2 gap-3">
        <StatCard titulo="Últimos 7 días" r={r7} />
        <StatCard titulo="Últimos 30 días" r={r30} />
      </div>

      {(r30?.error > 0 || r30?.rechazado > 0) && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          ⚠ Hay {(r30.error || 0) + (r30.rechazado || 0)} envío(s) con problema en los últimos 30 días. Filtra por “Solo problemas” para verlos.
        </p>
      )}

      {outbox && (
        <div className="bg-white rounded-2xl border shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Cola de comunicación (funnel)</h3>
            <button onClick={cargarOutbox} className="text-xs text-red-600 font-semibold">↻</button>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {Object.entries(outbox.resumen?.porEstado || {}).map(([est, n]) => (
              <span key={est} className={`text-xs font-bold rounded-full px-2 py-0.5 ${CHIP[est] || 'bg-gray-100 text-gray-500'}`}>{n} {est}</span>
            ))}
            {Object.keys(outbox.resumen?.porEstado || {}).length === 0 && (
              <span className="text-sm text-gray-400">Sin eventos con problema — la cola está sana.</span>
            )}
          </div>
          {outbox.eventos.length > 0 && (
            <div className="divide-y divide-gray-50 mt-2">
              {outbox.eventos.slice(0, 15).map(e => (
                <div key={e.id} className="py-2 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-bold rounded-full px-2 py-0.5 ${CHIP[e.estado] || 'bg-gray-100 text-gray-500'}`}>{e.estado}</span>
                    <span className="font-medium text-gray-700">{e.evento}</span>
                    {e.intentos > 0 && <span className="text-amber-700">{e.intentos} intento(s)</span>}
                    <span className="text-gray-400 ml-auto">{fecha(e.creadoEn)}</span>
                  </div>
                  <p className="text-gray-400 mt-0.5 truncate">{[e.destinatario, e.resultado || e.motivoDescarte || e.descartes].filter(Boolean).join(' · ')}</p>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-2">Muestra solo eventos con error, descartados o con reintentos. La cola completa está en Firebase → <code>eventos_comunicacion</code>.</p>
        </div>
      )}

      {data.resumen?.porTipo?.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm p-4">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Por tipo de correo</h3>
          <div className="space-y-1.5">
            {data.resumen.porTipo.map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="flex-1 min-w-0 truncate text-gray-700">{t.clave}</span>
                <span className="text-xs text-green-600 font-semibold flex-shrink-0">{t.enviado || 0} ok</span>
                {(t.rechazado || 0) > 0 && <span className="text-xs text-amber-700 font-semibold flex-shrink-0">{t.rechazado} rech.</span>}
                {(t.error || 0) > 0 && <span className="text-xs text-red-600 font-semibold flex-shrink-0">{t.error} err.</span>}
                <span className="text-xs text-gray-400 flex-shrink-0 w-24 text-right">{fecha(t.ultima)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={soloProblemas} onChange={e => setSoloProblemas(e.target.checked)} />
          Solo problemas (error / rechazado)
        </label>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por destinatario o asunto…"
          className="flex-1 min-w-[200px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
      </div>

      {!loading && filtrados.length === 0 && !error && (
        <p className="text-sm text-gray-400 text-center py-12">
          {soloProblemas ? '🎉 Sin envíos con problema' : 'Sin correos registrados todavía'}
        </p>
      )}

      {filtrados.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-sm divide-y divide-gray-50">
          {filtrados.map(e => (
            <button key={e.id} onClick={() => setPreviewId(e.id)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-bold rounded-full px-2 py-0.5 flex-shrink-0 ${CHIP[e.estado] || 'bg-gray-100 text-gray-500'}`}>{e.estado}</span>
                <span className="text-sm font-medium text-gray-800 flex-1 min-w-0 truncate">{e.subject || '(sin asunto)'}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{fecha(e.ts)}</span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {[e.to, e.tipo].filter(Boolean).join(' · ')}
              </p>
              {(e.error || e.rechazados) && (
                <p className="text-xs text-red-600 mt-1 break-words">{e.rechazados ? `Rechazado: ${e.rechazados} · ` : ''}{e.error}</p>
              )}
            </button>
          ))}
        </div>
      )}

      {previewId && <EmailPreviewModal id={previewId} onClose={() => setPreviewId(null)} />}
    </div>
  );
}
